import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Activity, FileText, Briefcase, CheckCircle2, Send, Coins, UserPlus, Edit3, RotateCw } from "lucide-react";

const ACTION_META = {
  // Mission audit
  MISSION_CREATED: { icon: Briefcase, label: "Mission created", color: "text-blue-600 bg-blue-50" },
  MISSION_UPDATED: { icon: Edit3, label: "Mission updated", color: "text-zinc-600 bg-zinc-50" },
  MISSION_VALIDATED: { icon: CheckCircle2, label: "Mission validated", color: "text-emerald-600 bg-emerald-50" },
  MISSION_SUBMITTED: { icon: Send, label: "Mission soumise au CEO", color: "text-amber-600 bg-amber-50" },
  MISSION_CLOSED: { icon: CheckCircle2, label: "Mission closed", color: "text-orange-600 bg-orange-50" },
  MISSION_REOPENED: { icon: RotateCw, label: "Mission reopened", color: "text-blue-600 bg-blue-50" },
  MISSION_MANAGER_ASSIGNED: { icon: UserPlus, label: "Manager assigned", color: "text-blue-600 bg-blue-50" },
  ROLE_ADDED: { icon: UserPlus, label: "Role added", color: "text-blue-600 bg-blue-50" },
  ROLE_UPDATED: { icon: Edit3, label: "Role updated", color: "text-zinc-600 bg-zinc-50" },
  ROLE_DELETED: { icon: Edit3, label: "Role deleted", color: "text-red-600 bg-red-50" },
  ASSIGNMENT_ADDED: { icon: UserPlus, label: "Assignment added", color: "text-blue-600 bg-blue-50" },
  ASSIGNMENT_UPDATED: { icon: Edit3, label: "Assignment updated", color: "text-zinc-600 bg-zinc-50" },
  ASSIGNMENT_REMOVED: { icon: Edit3, label: "Assignment removed", color: "text-red-600 bg-red-50" },
  // Invoice events
  INVOICE_GENERATED: { icon: FileText, label: "Invoice generated", color: "text-blue-700 bg-blue-50" },
  INVOICE_SENT: { icon: Send, label: "Invoice sent to client", color: "text-blue-700 bg-blue-100" },
  INVOICE_PAID: { icon: Coins, label: "Invoice paid", color: "text-emerald-700 bg-emerald-50" },
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date();
    const diffH = (today - d) / 36e5;
    if (diffH < 1) return `il y a ${Math.max(1, Math.floor(diffH * 60))} min`;
    if (diffH < 24) return `il y a ${Math.floor(diffH)} h`;
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function RecentActivityBlock({ activeGroups = null }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/recent-activity?limit=80").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  if (rows === null) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="recent-activity-block">
        <div className="text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }

  // Optional client-side filter using the `group` field tagged by the backend.
  const visible = activeGroups ? rows.filter((r) => activeGroups.has(r.group)) : rows;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="recent-activity-block">
      <div className="flex items-center gap-3 mb-4">
        <Activity size={18} className="text-brand-navy" />
        <h2 className="font-heading text-lg font-bold tracking-tight">Recent activity</h2>
        <span className="ml-auto text-xs font-mono text-zinc-500">{visible.length} event{visible.length > 1 ? "s" : ""}</span>
      </div>

      {visible.length === 0 && (
        <div className="text-sm text-zinc-500 text-center py-8" data-testid="recent-activity-empty">No recent activity.</div>
      )}

      <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
        {visible.map((it, idx) => {
          const meta = ACTION_META[it.action] || { icon: Activity, label: it.action, color: "text-zinc-600 bg-zinc-50" };
          const Icon = meta.icon;
          const inner = (
            <div className="flex items-start gap-3 px-3 py-2.5 rounded-md hover:bg-zinc-50 transition-colors" data-testid={`activity-row-${idx}`}>
              <div className={`p-1.5 rounded ${meta.color} shrink-0`}>
                <Icon size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-zinc-700">{meta.label}</span>
                  {it.color && <span className="w-1.5 h-1.5 rounded-full" style={{ background: it.color }} />}
                  <span className="text-sm font-medium text-zinc-900 truncate">{it.title}</span>
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                  {it.actor_name || "—"} · {fmtDateTime(it.created_at)}
                </div>
              </div>
            </div>
          );
          return it.link ? (
            <Link key={idx} to={it.link} className="block" data-testid={`activity-link-${idx}`}>{inner}</Link>
          ) : (
            <div key={idx}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}
